import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil, Plus, Save, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/useToast'
import { templatesApi, extractApiError } from '@/lib/api'
import type { LabelCodeType, LabelTemplateAssignment, LabelTemplateSummary } from '@/types'
import { LABEL_CODE_TYPE_LABELS } from '@/types'

const CODE_TYPES: LabelCodeType[] = ['km', 'ka_box', 'ka_pallet']

function assignmentFromTemplate(t: LabelTemplateSummary): LabelTemplateAssignment {
  return {
    for_km: t.for_km,
    for_ka_box: t.for_ka_box,
    for_ka_pallet: t.for_ka_pallet,
    is_default_km: t.is_default_km,
    is_default_ka_box: t.is_default_ka_box,
    is_default_ka_pallet: t.is_default_ka_pallet,
  }
}

function codeTypeBadges(t: LabelTemplateSummary) {
  const items: string[] = []
  if (t.for_km) items.push(LABEL_CODE_TYPE_LABELS.km)
  if (t.for_ka_box) items.push(LABEL_CODE_TYPE_LABELS.ka_box)
  if (t.for_ka_pallet) items.push(LABEL_CODE_TYPE_LABELS.ka_pallet)
  return items
}

function defaultBadges(t: LabelTemplateSummary) {
  const items: string[] = []
  if (t.is_default_km) items.push(LABEL_CODE_TYPE_LABELS.km)
  if (t.is_default_ka_box) items.push(LABEL_CODE_TYPE_LABELS.ka_box)
  if (t.is_default_ka_pallet) items.push(LABEL_CODE_TYPE_LABELS.ka_pallet)
  return items
}

export function LabelTemplatesPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<LabelTemplateAssignment | null>(null)

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await templatesApi.getAll()).data ?? [],
  })

  const selected = useMemo(
    () => templates.find((t) => t.template_id === selectedId) ?? null,
    [templates, selectedId],
  )

  useEffect(() => {
    if (selected) {
      setDraft(assignmentFromTemplate(selected))
    } else {
      setDraft(null)
    }
  }, [selected])

  const saveAssignmentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: LabelTemplateAssignment }) =>
      templatesApi.updateAssignment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['label-templates'] })
      toast.success('Назначение сохранено')
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['label-templates'] })
      setSelectedId(null)
      toast.success('Шаблон удалён')
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const handleCreate = () => navigate('/admin/label-editor')

  const handleEdit = () => {
    if (!selected) {
      toast.error('Выберите шаблон в таблице')
      return
    }
    navigate(`/admin/label-editor/${encodeURIComponent(selected.template_id)}`)
  }

  const handleDelete = () => {
    if (!selected) {
      toast.error('Выберите шаблон в таблице')
      return
    }
    if (!confirm(`Удалить шаблон «${selected.name}»?`)) return
    deleteMutation.mutate(selected.template_id)
  }

  const toggleFor = (type: LabelCodeType) => {
    if (!draft) return
    const key = type === 'km' ? 'for_km' : type === 'ka_box' ? 'for_ka_box' : 'for_ka_pallet'
    const defaultKey =
      type === 'km' ? 'is_default_km' : type === 'ka_box' ? 'is_default_ka_box' : 'is_default_ka_pallet'
    const next = !draft[key]
    setDraft({
      ...draft,
      [key]: next,
      [defaultKey]: next ? draft[defaultKey] : false,
    })
  }

  const toggleDefault = (type: LabelCodeType) => {
    if (!draft) return
    const forKey = type === 'km' ? 'for_km' : type === 'ka_box' ? 'for_ka_box' : 'for_ka_pallet'
    const defaultKey =
      type === 'km' ? 'is_default_km' : type === 'ka_box' ? 'is_default_ka_box' : 'is_default_ka_pallet'
    if (!draft[forKey]) return
    setDraft({
      ...draft,
      [defaultKey]: !draft[defaultKey],
    })
  }

  const handleSaveAssignment = () => {
    if (!selected || !draft) return
    saveAssignmentMutation.mutate({ id: selected.template_id, data: draft })
  }

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/settings">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Шаблоны этикеток</h1>
          <p className="text-sm text-gray-500 mt-0.5">Назначение шаблонов для типов кодов и печати</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-1" /> Создать
        </Button>
        <Button variant="secondary" onClick={handleEdit} disabled={!selected}>
          <Pencil className="w-4 h-4 mr-1" /> Редактировать
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={!selected || deleteMutation.isPending}>
          <Trash2 className="w-4 h-4 mr-1" /> Удалить
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Название</th>
              <th className="px-4 py-3">Размер, мм</th>
              <th className="px-4 py-3">Для кодов</th>
              <th className="px-4 py-3">По умолчанию</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {templates.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-gray-400">
                  Нет шаблонов. Нажмите «Создать», чтобы добавить первый.
                </td>
              </tr>
            ) : (
              templates.map((t) => {
                const isSelected = selectedId === t.template_id
                const purposes = codeTypeBadges(t)
                const defaults = defaultBadges(t)
                return (
                  <tr
                    key={t.template_id}
                    onClick={() => setSelectedId(t.template_id)}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3 font-medium">{t.name}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {Math.round(t.width)} × {Math.round(t.height)}
                    </td>
                    <td className="px-4 py-3">
                      {purposes.length ? (
                        <div className="flex flex-wrap gap-1">
                          {purposes.map((label) => (
                            <span
                              key={label}
                              className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {defaults.length ? (
                        <div className="flex flex-wrap gap-1">
                          {defaults.map((label) => (
                            <span
                              key={label}
                              className="inline-block px-2 py-0.5 rounded-full bg-green-100 text-xs text-green-700 font-medium"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {selected && draft && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-base font-semibold mb-3">Назначение: {selected.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {CODE_TYPES.map((type) => {
              const forKey = type === 'km' ? 'for_km' : type === 'ka_box' ? 'for_ka_box' : 'for_ka_pallet'
              const defaultKey =
                type === 'km' ? 'is_default_km' : type === 'ka_box' ? 'is_default_ka_box' : 'is_default_ka_pallet'
              return (
                <div key={type} className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">{LABEL_CODE_TYPE_LABELS[type]}</p>
                  <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                    <input type="checkbox" checked={draft[forKey]} onChange={() => toggleFor(type)} />
                    Используется для этого типа
                  </label>
                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      draft[forKey] ? '' : 'opacity-40 pointer-events-none'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={draft[defaultKey]}
                      onChange={() => toggleDefault(type)}
                      disabled={!draft[forKey]}
                    />
                    Шаблон по умолчанию
                  </label>
                </div>
              )
            })}
          </div>
          <Button onClick={handleSaveAssignment} isLoading={saveAssignmentMutation.isPending}>
            <Save className="w-4 h-4 mr-1" /> Сохранить назначение
          </Button>
        </div>
      )}
    </div>
  )
}
